import React from 'react';
import './PageGenerationProgress.css';

interface ProgressStep {
  id: string;
  message: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  error?: string;
}

interface BatchSummary {
  success: number;
  failed: number;
  skipped: number;
  total: number;
  wasCancelled: boolean;
}

interface PageGenerationProgressProps {
  steps: ProgressStep[];
  isOpen: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  isCancelling?: boolean;
  batchProgress?: { current: number; total: number } | null;
  batchSummary?: BatchSummary | null;
  currentPageNumber?: number | null;
}

const PageGenerationProgress: React.FC<PageGenerationProgressProps> = ({ steps, isOpen, onClose, onCancel, isCancelling, batchProgress, batchSummary, currentPageNumber }) => {
  if (!isOpen) return null;

  // Check if generation is complete (all steps done) or has error
  const isComplete = steps.every(step => step.status === 'completed' || step.status === 'error');
  const hasError = steps.some(step => step.status === 'error');
  const isInProgress = steps.some(step => step.status === 'in-progress');
  const showSummary = batchSummary !== null && batchSummary !== undefined;

  return (
    <div className="modal-overlay">
      <div className="modal-content progress-modal">
        <div className="modal-header">
          <h2>
            {showSummary 
              ? '📚 Batch Generation Complete' 
              : batchProgress 
                ? `🎨 Generating Page ${batchProgress.current} of ${batchProgress.total}` 
                : currentPageNumber 
                  ? `🎨 Generating Page ${currentPageNumber}`
                  : '🎨 Generating Page'}
            {isCancelling && !showSummary && ' (Cancelling...)'}
          </h2>
        </div>
        
        <div className="modal-body">
          {/* Show summary if batch generation is complete */}
          {showSummary && (
            <div className="batch-summary" style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
                  <span style={{ color: '#16a34a' }}>✅</span>
                  <span>Successfully generated: <strong>{batchSummary.success}</strong> page(s)</span>
                </div>
                
                {batchSummary.failed > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
                    <span style={{ color: '#dc2626' }}>❌</span>
                    <span>Failed: <strong>{batchSummary.failed}</strong> page(s)</span>
                  </div>
                )}
                
                {batchSummary.wasCancelled && batchSummary.skipped > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
                    <span style={{ color: '#f59e0b' }}>⏸️</span>
                    <span>Cancelled - Skipped: <strong>{batchSummary.skipped}</strong> remaining page(s)</span>
                  </div>
                )}
                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  fontSize: '16px',
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: '1px solid #86efac'
                }}>
                  <span>📖</span>
                  <span>Total pages in storybook: <strong>{batchSummary.total}</strong></span>
                </div>
              </div>
            </div>
          )}
          
          <div className="progress-steps">
            {steps.map((step, index) => (
              <div key={step.id} className={`progress-step ${step.status}`}>
                <div className="step-icon">
                  {step.status === 'completed' && <span className="icon-check">✓</span>}
                  {step.status === 'in-progress' && <span className="icon-spinner">⟳</span>}
                  {step.status === 'error' && <span className="icon-error">✕</span>}
                  {step.status === 'pending' && <span className="icon-pending">○</span>}
                </div>
                <div className="step-content">
                  <div className="step-message">{step.message}</div>
                  {step.error && <div className="step-error">{step.error}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="modal-footer" style={{ 
          padding: '15px 20px', 
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'center',
          gap: '12px'
        }}>
          {/* Cancel button - show when in progress and not already cancelling */}
          {isInProgress && onCancel && !isCancelling && (
            <button
              onClick={onCancel}
              className="btn btn-secondary"
              style={{
                padding: '10px 30px',
                fontSize: '16px',
                fontWeight: '600',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              ✕ Cancel
            </button>
          )}
          
          {/* Close button - show when complete, cancelled, or summary is shown */}
          {(isComplete || isCancelling || showSummary) && onClose && (
            <button
              onClick={onClose}
              className="btn btn-primary"
              style={{
                padding: '10px 30px',
                fontSize: '16px',
                fontWeight: '600',
                backgroundColor: hasError || (batchSummary?.wasCancelled) ? '#dc2626' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              {showSummary ? '✓ Done' : hasError ? 'Close' : isCancelling ? 'Close' : '✓ Close'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageGenerationProgress;
